import axiosInstance from '../utils/axio-instance';
import { addToast } from './ui.actions';

/**
 * Reviews & ratings — talks to the main API's /reviews module.
 * Vendor rating aggregates are denormalized onto the ES people doc by the
 * backend, so search/matching pick them up automatically.
 */

export const fetchVendorReviews = (vendorUserId, page = 1, size = 10) => async (dispatch) => {
  dispatch({ type: 'VENDOR_REVIEWS_LOADING', payload: true });
  try {
    const response = await axiosInstance.get(`/reviews/vendor/${vendorUserId}`, {
      params: { page, size },
    });
    dispatch({
      type: 'VENDOR_REVIEWS',
      payload: { vendorUserId, page, ...response.data.data },
    });
  } catch (error) {
    console.error('Error fetching vendor reviews:', error);
    dispatch({ type: 'VENDOR_REVIEWS_LOADING', payload: false });
  }
};

export const fetchVendorReviewSummary = (vendorUserId) => async (dispatch) => {
  try {
    const response = await axiosInstance.get(`/reviews/vendor/${vendorUserId}/summary`);
    dispatch({
      type: 'VENDOR_REVIEW_SUMMARY',
      payload: { vendorUserId, ...response.data.data },
    });
  } catch (error) {
    console.error('Error fetching review summary:', error);
  }
};

export const createReview = (reviewObj) => async (dispatch) => {
  try {
    const response = await axiosInstance.post('/reviews', reviewObj);
    dispatch(addToast({
      title: 'Review submitted',
      description: 'Thank you for sharing your experience.',
      status: 'success',
    }));
    // Refresh the vendor's list + summary so open views update.
    dispatch(fetchVendorReviews(reviewObj.vendorUserId));
    return { success: true, data: response.data.data };
  } catch (error) {
    const message = error?.response?.data?.error || 'Failed to submit review. Please try again later.';
    dispatch(addToast({ title: 'Review not submitted', description: message, status: 'error' }));
    return { success: false, error: message };
  }
};

export const deleteReview = (reviewId, vendorUserId) => async (dispatch) => {
  try {
    await axiosInstance.delete(`/reviews/${reviewId}`);
    dispatch(addToast({ title: 'Review deleted', status: 'success' }));
    dispatch(fetchVendorReviews(vendorUserId));
    return { success: true };
  } catch (error) {
    const message = error?.response?.data?.error || 'Failed to delete review.';
    dispatch(addToast({ title: 'Delete failed', description: message, status: 'error' }));
    return { success: false, error: message };
  }
};

// Matched vendors for a request — powers the popup after request creation.
export const fetchMatchedVendors = (requestId, { from = 0, size = 10, verifiedOnly = false } = {}) => async (dispatch) => {
  dispatch({ type: 'MATCHED_VENDORS_LOADING', payload: true });
  try {
    const response = await axiosInstance.get(`/property/requests/${requestId}/matched-vendors`, {
      params: { from, size, verifiedOnly },
    });
    dispatch({
      type: 'MATCHED_VENDORS',
      payload: { items: response.data.data, total: response.data.total },
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error('Error fetching matched vendors:', error);
    dispatch({ type: 'MATCHED_VENDORS_LOADING', payload: false });
    return { success: false };
  }
};

// Consolidated responses for a request (single "view all responses" action).
export const fetchAllResponses = (requestId, page = 0, size = 20) => async (dispatch) => {
  dispatch({ type: 'ALL_RESPONSES_LOADING', payload: true });
  try {
    const response = await axiosInstance.get(`/property/requests/${requestId}/responses`, {
      params: { page, size },
    });
    dispatch({
      type: 'ALL_RESPONSES',
      payload: { requestId, items: response.data.data, totalCount: response.data.totalCount },
    });
    return { success: true };
  } catch (error) {
    console.error('Error fetching all responses:', error);
    dispatch({ type: 'ALL_RESPONSES_LOADING', payload: false });
    dispatch(addToast({
      title: 'Error',
      description: 'Failed to load responses. Please try again later.',
      status: 'error',
    }));
    return { success: false };
  }
};

// Has the current buyer already reviewed this vendor (for a request/response)?
// Returns { exists, review } — plain promise, no redux state needed.
export const checkReviewExists = async (vendorUserId, requestId, responseId) => {
  try {
    const response = await axiosInstance.get('/reviews/check', {
      params: { vendorUserId, requestId, responseId },
    });
    return response.data.data;
  } catch (error) {
    console.error('Error checking review:', error);
    return { exists: false };
  }
};

// Buyer skipped the review popup after mark-as-done — email them a link.
export const sendReviewReminder = (vendorUserId, vendorName, requestId, responseId) => async (dispatch) => {
  try {
    const response = await axiosInstance.post('/reviews/remind', {
      vendorUserId: Number(vendorUserId),
      vendorName: vendorName || '',
      ...(requestId ? { requestId: String(requestId) } : {}),
      ...(responseId ? { responseId: String(responseId) } : {}),
    });
    if (response.data?.data?.sent) {
      dispatch(addToast({
        title: 'No problem!',
        description: 'We emailed you a link so you can write the review later.',
        status: 'info',
      }));
    }
    return { success: true };
  } catch (error) {
    console.error('Error sending review reminder:', error);
    return { success: false };
  }
};

// Buyer lifecycle status change for their request/response
// (active | in_progress | paused | done | closed | archived).
export const updateRecordStatus = (publicId, status, recordType = 'request') => async (dispatch) => {
  try {
    await axiosInstance.put(`/property/requests/${publicId}/status`, { status, recordType });
    dispatch(addToast({
      title: 'Status updated',
      description: `Marked as ${status.replace(/_/g, ' ')}.`,
      status: 'success',
    }));
    return { success: true };
  } catch (error) {
    const message = error?.response?.data?.error || 'Failed to update status.';
    dispatch(addToast({ title: 'Error', description: message, status: 'error' }));
    return { success: false, error: message };
  }
};

// Admin: mark a professional as verified / unverified.
export const verifyUser = (userId, isVerified, notes) => async (dispatch) => {
  try {
    await axiosInstance.put(`/users/${userId}/verify`, {
      is_verified: isVerified,
      notes: notes || '',
    });
    dispatch(addToast({
      title: isVerified ? 'User marked as verified' : 'User verification removed',
      status: 'success',
    }));
    return { success: true };
  } catch (error) {
    const message = error?.response?.data?.error || 'Failed to update verification.';
    dispatch(addToast({ title: 'Verification failed', description: message, status: 'error' }));
    return { success: false, error: message };
  }
};
